import * as yup from "yup";
import React, { useState } from "react";

import { Box, Grid, TextField } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import Title from "../../../common/Title";

import CallFilters from "./CallFilters";
import MessageFilters from "./MessageFilters";
import useAxios from "../../../../hooks/useAxios";
import useAlert from "../../../../hooks/useAlert";
import {
  __getTmobilejCallFilters,
  __getTmobilejMessageFilter,
} from "../../../../api/sim";

const lineValidationSchema = yup.object({
  number: yup
    .string("number")
    .min(10, "Should be exactly 10 digits long")
    .max(10, "Should be exactly 10 digits long")
    .required(),
});

const TmobilejFilters = () => {
  const { setAlertMessage } = useAlert();
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [callFilterData, setCallFilterData] = useState(null);
  const [messageFilterData, setMessageFilterData] = useState(null);
  const [showCallFilters, setShowCallFilters] = useState(false);
  const [showMessageFilters, setShowMessageFilters] = useState(false);

  const [fetchCallResp, fetchCallApi, fetchCallLoading] = useAxios(
    __getTmobilejCallFilters(phone)
  );
  const [fetchMsgResp, fetchMsgApi, fetchMsgLoading] = useAxios(
    __getTmobilejMessageFilter(phone)
  );

  const validatePhone = (val) => {
    if (!val) return "Phone number is required";
    const obtainedData = { number: String(val || "").trim() };
    try {
      lineValidationSchema.validateSync(obtainedData);
    } catch (e) {
      return "Phone number must be digits-only and 10 digits";
    }
    if (isNaN(obtainedData.number)) return "Phone number must be digits-only and 10 digits";
    return "";
  };

  React.useEffect(() => {
    if (fetchCallResp?.status && fetchCallResp?.data) {
      setCallFilterData(fetchCallResp.data);
      setShowCallFilters(true);
      setShowMessageFilters(false); // Hide message filters when call filters are fetched
    }
  }, [fetchCallResp]);

  React.useEffect(() => {
    if (fetchMsgResp?.status && fetchMsgResp?.data) {
      setMessageFilterData(fetchMsgResp.data);
      setShowMessageFilters(true);
      setShowCallFilters(false); // Hide call filters when message filters are fetched
    }
  }, [fetchMsgResp]);

  const handleFetchCallFilters = async () => {
    const err = validatePhone(phone);
    setPhoneError(err);
    if (err) {
      setAlertMessage({ type: "error", message: err });
      return;
    }
    setShowMessageFilters(false); // Hide message filters immediately when call filters button is clicked
    await fetchCallApi();
  };

  const handleFetchMessageFilters = async () => {
    const err = validatePhone(phone);
    setPhoneError(err);
    if (err) {
      setAlertMessage({ type: "error", message: err });
      return;
    }
    setShowCallFilters(false); // Hide call filters immediately when message filters button is clicked
    await fetchMsgApi();
  };

  return (
    <Box sx={{ width: "100%" }} display="flex" alignItems="center" flexDirection="column">
      <Title variant="h5" content="T-Mobile J Filters" sx={{ mb: 3 }} gutterBottom />

      <Grid container spacing={2} direction="column" sx={{ maxWidth: 700 }}>
        <Grid item>
          <TextField
            fullWidth
            label="Phone Number (10 digits)"
            value={phone}
            error={Boolean(phoneError)}
            helperText={phoneError || "Digits only (e.g. 1234567890)"}
            onChange={(e) => {
              const v = e.target.value.trim();
              setPhone(v);
              setPhoneError(v ? validatePhone(v) : "");
            }}
          />
        </Grid>

        <Grid item sx={{ display: "flex", gap: 1 }}>
          <LoadingButton
            loading={fetchCallLoading}
            disabled={fetchCallLoading || Boolean(phoneError) || !phone}
            variant={showCallFilters ? "contained" : "outlined"}
            onClick={handleFetchCallFilters}
          >
            Fetch Call Filters
          </LoadingButton>
          <LoadingButton
            loading={fetchMsgLoading}
            disabled={fetchMsgLoading || Boolean(phoneError) || !phone}
            variant={showMessageFilters ? "contained" : "outlined"}
            onClick={handleFetchMessageFilters}
          >
            Fetch Message Filters
          </LoadingButton>
        </Grid>

        {showCallFilters && (
          <CallFilters
            phone={phone}
            phoneError={phoneError}
            validatePhone={validatePhone}
            initialData={callFilterData}
            onDataUpdate={setCallFilterData}
          />
        )}

        {showMessageFilters && (
          <MessageFilters
            phone={phone}
            phoneError={phoneError}
            validatePhone={validatePhone}
            initialData={messageFilterData}
            onDataUpdate={setMessageFilterData}
          />
        )}
      </Grid>
    </Box>
  );
};

export default TmobilejFilters;
